import { apiClient } from '../utils/apiClient';

// Payment Verification Request Interface
export interface PaymentVerificationRequest {
  razorpay_order_id: string;
  razorpay_payment_id: string;
  razorpay_signature: string;
}

// Payment Verification Response Interface
export interface PaymentVerificationResponse {
  id: string;
  booking_id: number;
  amount: number;
  payment_mode: string;
  payment_status: string;
  razorpay_order_id: string;
  razorpay_payment_id: string;
  created_at: string;
}

// Razorpay Response Interface
export interface RazorpayResponse {
  razorpay_payment_id: string;
  razorpay_order_id: string;
  razorpay_signature: string;
}

// Razorpay Options Interface
export interface RazorpayOptions {
  key: string;
  amount: number;
  currency: string;
  name: string;
  description: string;
  order_id: string;
  handler: (response: RazorpayResponse) => void;
  prefill: {
    name: string;
    email: string;
    contact: string;
  };
  notes: {
    address: string;
  };
  theme: {
    color: string;
  };
}

export class PaymentService {
  // Verify payment with backend
  static async verifyPayment(paymentData: PaymentVerificationRequest): Promise<PaymentVerificationResponse | null> {
    try {
      const response = await apiClient.post('/api/booking-orders/verify-order-payment', paymentData);
      return response?.data || null;
    } catch (error) {
      console.error('Payment verification error:', error);
      throw new Error(`Payment verification failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }
  
  // Load Razorpay script dynamically (already preloaded in index.html)
  static loadRazorpayScript(): Promise<boolean> {
    return new Promise((resolve) => {
      // Wait for Razorpay to be available (preloaded in HTML)
      let attempts = 0;
      const checkRazorpay = setInterval(() => {
        if (window.Razorpay) {
          clearInterval(checkRazorpay);
          console.log('✅ Razorpay loaded');
          resolve(true);
        }
        attempts++;
        if (attempts > 50) { // 5 seconds timeout
          clearInterval(checkRazorpay);
          console.error('❌ Razorpay failed to load');
          resolve(false);
        }
      }, 100);
    });
  }
  
  // Initialize Razorpay payment
  static async initiateRazorpayPayment(options: RazorpayOptions): Promise<void> {
    return new Promise((resolve, reject) => {
      console.log('=== RAZORPAY PAYMENT SERVICE DEBUG ===');
      console.log('Payment options:', options);
      
      // Validate key before proceeding
      if (!options.key || options.key === 'undefined') {
        console.error('❌ Razorpay key is invalid:', options.key);
        return reject(new Error('Invalid Razorpay key. Please contact support.'));
      }
      
      this.loadRazorpayScript().then((scriptLoaded) => {
        console.log('Razorpay script loaded:', scriptLoaded);
        
        if (!scriptLoaded) {
          console.error('❌ Razorpay script failed to load');
          return reject(new Error('Payment gateway failed to load. Please try again.'));
        }
        
        console.log('Window.Razorpay available:', !!window.Razorpay);
        
        if (!window.Razorpay) {
          console.error('❌ Razorpay object not available on window');
          return reject(new Error('Payment gateway not initialized properly.'));
        }
        
        try {
          console.log('Creating Razorpay instance with options:', {
            key: options.key,
            key_length: options.key.length,
            order_id: options.order_id,
            amount: options.amount,
            currency: options.currency
          });
          
          const razorpay = new window.Razorpay(options);
          
          // Track if payment was handled
          let paymentHandled = false;
          
          // Handle successful payment (handler is called on success)
          // The handler is already defined in options, it will be called on success
          const originalHandler = options.handler;
          options.handler = (response: any) => {
            console.log('✅ RAZORPAY EVENT: handler called (payment successful)');
            paymentHandled = true;
            resolve(undefined); // Resolve the promise as payment was successful
            // Call original handler for payment verification
            originalHandler(response);
          };
          
          // Update handler in razorpay instance
          const updatedRazorpay = new window.Razorpay(options);
          
          // Handle payment failure
          updatedRazorpay.on('payment.failed', (response: any) => {
            console.error('❌ RAZORPAY EVENT: payment.failed triggered', response);
            if (!paymentHandled) {
              paymentHandled = true;
              const errorMsg = `Payment failed: ${response.error.description}`;
              console.error('❌ Rejecting promise with error:', errorMsg);
              reject(new Error(errorMsg));
            }
          });
          
          // Handle payment window closure/cancellation
          updatedRazorpay.on('payment.closed', () => {
            console.warn('⚠️ RAZORPAY EVENT: payment.closed triggered - User closed the modal');
            if (!paymentHandled) {
              paymentHandled = true;
              const errorMsg = 'Payment was cancelled. Your booking has been saved but payment is pending. Please complete the payment to confirm your booking.';
              console.warn('⚠️ Rejecting promise with error:', errorMsg);
              reject(new Error(errorMsg));
            }
          });
          
          console.log('Opening Razorpay payment modal...');
          updatedRazorpay.open();
        } catch (error) {
          console.error('Error creating/opening Razorpay:', error);
          reject(error);
        }
      }).catch((error) => {
        console.error('Error loading Razorpay script:', error);
        reject(error);
      });
    });
  }
}

// Declare Razorpay global
declare global {
  interface Window {
    Razorpay: any;
  }
}